export interface MedicationSearchResult {
  rxcui: string;
  name: string;
  synonym?: string;
  tty?: string; // Term Type
  language?: string;
}

export interface MedicationDetails {
  rxcui: string;
  name: string;
  synonyms: string[];
  brandNames: string[];
  genericName?: string;
  strength?: string;
  dosageForm?: string;
  route?: string;
}

class MedicationApiService {
  private readonly baseUrl = 'https://rxnav.nlm.nih.gov/REST';

  // Focus on medication term types that represent recognizable medications
  private readonly validMedicationTTY = [
    'BN',   // Brand Name (e.g., "Tylenol", "Advil")
    'PSN',  // Prescribable Name
    'SBD',  // Semantic Branded Drug (e.g., "Tylenol 325 MG Oral Tablet")
    'GPCK', // Generic Pack
    'BPCK', // Branded Pack
    'PIN'   // Precise Ingredient (for single ingredients)
  ];

  // Amino acids and complex nutritional components to filter out
  private readonly aminoAcidsAndNutrients = [
    'alanine', 'arginine', 'asparagine', 'aspartate', 'aspartic acid',
    'cysteine', 'glutamate', 'glutamic acid', 'glutamine', 'glycine',
    'histidine', 'isoleucine', 'leucine', 'lysine', 'methionine',
    'phenylalanine', 'proline', 'serine', 'threonine', 'tryptophan',
    'tyrosine', 'valine', 'calcium chloride', 'magnesium chloride',
    'sodium chloride', 'potassium phosphate', 'sodium acetate',
    'phosphoric acid', 'sodium bisulfite'
  ];

  async searchMedications(query: string): Promise<MedicationSearchResult[]> {
    console.log('🔍 API SERVICE SEARCH START 🔍');
    console.log('Query received:', JSON.stringify(query));
    
    if (!query || query.trim().length < 2) {
      console.log('❌ Query too short, returning empty results');
      return [];
    }

    try {
      // Use different RxNorm endpoint for better results
      const searchUrl = `${this.baseUrl}/drugs.json?name=${encodeURIComponent(query.trim())}`;
      console.log('🌐 Making request to URL:', searchUrl);
      
      const response = await fetch(searchUrl);
      console.log('📡 Fetch completed - Status:', response.status);
      
      if (!response.ok) {
        console.error('❌ RxNorm API request failed');
        // Try alternative endpoint
        console.log('🔄 Trying alternative endpoint...');
        const altUrl = `${this.baseUrl}/rxcui.json?name=${encodeURIComponent(query.trim())}&search=2`;
        
        const altResponse = await fetch(altUrl);
        if (!altResponse.ok) {
          console.error('❌ Alternative endpoint also failed');
          return [];
        }
        
        const altData = await altResponse.json();
        console.log('🔄 Alternative response data received');
        return this.processAlternativeResponse(altData, query);
      }

      const data = await response.json();
      console.log('📊 Primary API response received');
      
      return this.processPrimaryResponse(data, query);

    } catch (error) {
      console.error('💥 ERROR in searchMedications:', error);
      return [];
    }
  }

  private async processPrimaryResponse(data: any, query: string): Promise<MedicationSearchResult[]> {
    console.log('🔄 Processing primary response...');
    
    if (!data.drugGroup?.conceptGroup) {
      console.log('❌ No drugGroup.conceptGroup found in response');
      return [];
    }

    const conceptGroups = Array.isArray(data.drugGroup.conceptGroup) 
      ? data.drugGroup.conceptGroup 
      : [data.drugGroup.conceptGroup];

    console.log('Number of concept groups:', conceptGroups.length);

    const allResults: MedicationSearchResult[] = [];

    for (let i = 0; i < conceptGroups.length; i++) {
      const group = conceptGroups[i];
      console.log(`📋 Processing group ${i + 1}`);
      
      if (!group.conceptProperties) {
        console.log(`⚠️ Group ${i + 1} has no conceptProperties`);
        continue;
      }

      const concepts = Array.isArray(group.conceptProperties) 
        ? group.conceptProperties 
        : [group.conceptProperties];

      console.log(`Group ${i + 1} has ${concepts.length} concepts`);

      for (let j = 0; j < concepts.length && j < 20; j++) {
        const concept = concepts[j];

        if (!concept.rxcui || !concept.name) {
          console.log(`  ❌ Concept ${j + 1} missing rxcui or name`);
          continue;
        }

        // Check if it's a valid medication type
        if (concept.tty && !this.validMedicationTTY.includes(concept.tty)) {
          console.log(`  🚫 Filtering out concept ${j + 1} due to TTY: ${concept.tty}`);
          continue;
        }

        // Filter out amino acid compositions and complex IV solutions
        if (this.isAminoAcidComposition(concept.name)) {
          console.log(`  🚫 Filtering out amino acid composition: ${concept.name.substring(0, 50)}...`);
          continue;
        }

        // Filter out complex injectable solutions with multiple components
        if (this.isComplexInjectableSolution(concept.name)) {
          console.log(`  🚫 Filtering out complex injectable: ${concept.name.substring(0, 50)}...`);
          continue;
        }

        const result: MedicationSearchResult = {
          rxcui: concept.rxcui,
          name: concept.name,
          tty: concept.tty,
          language: concept.language || 'ENG'
        };

        console.log(`  ✅ Including concept ${j + 1}:`, result.name);
        allResults.push(result);
      }
    }

    console.log('🎯 Final results before sorting:', allResults.length);
    const sortedResults = this.sortResults(allResults, query);
    console.log('🏆 Final sorted results:', sortedResults.length);
    
    return sortedResults;
  }

  private isAminoAcidComposition(name: string): boolean {
    const nameLower = name.toLowerCase();
    
    // Check if it contains multiple amino acids (sign of a composition)
    const aminoAcidCount = this.aminoAcidsAndNutrients.filter(aa => 
      nameLower.includes(aa.toLowerCase())
    ).length;
    
    // If it contains 3 or more amino acids/nutrients, it's likely a composition
    return aminoAcidCount >= 3;
  }

  private isComplexInjectableSolution(name: string): boolean {
    const nameLower = name.toLowerCase();
    
    // Check for indicators of complex injectable solutions
    const complexIndicators = [
      'injectable solution',
      'mg/ml',
      'meq/ml',
      '/ ',  // Multiple components separated by slashes
    ];
    
    // Count how many components are separated by " / "
    const componentCount = (name.match(/ \/ /g) || []).length;
    
    // If it has many components or specific indicators, filter it out
    return componentCount >= 5 || complexIndicators.some(indicator => 
      nameLower.includes(indicator.toLowerCase())
    );
  }

  private async processAlternativeResponse(data: any, query: string): Promise<MedicationSearchResult[]> {
    console.log('🔄 Processing alternative response...');
    
    if (!data.idGroup?.rxnormId) {
      console.log('❌ No rxnormId found in alternative response');
      return [];
    }

    const rxcuis = Array.isArray(data.idGroup.rxnormId) 
      ? data.idGroup.rxnormId 
      : [data.idGroup.rxnormId];

    console.log('🔍 Found RXCUIs:', rxcuis.length);

    const results: MedicationSearchResult[] = [];

    for (let i = 0; i < Math.min(rxcuis.length, 5); i++) {
      const rxcui = rxcuis[i];
      
      try {
        const detailUrl = `${this.baseUrl}/rxcui/${rxcui}/properties.json`;
        const detailResponse = await fetch(detailUrl);
        
        if (!detailResponse.ok) {
          console.log(`  ❌ Failed to fetch details for RXCUI: ${rxcui}`);
          continue;
        }
        
        const detailData = await detailResponse.json();
        
        if (!detailData.properties) {
          console.log(`  ❌ No properties found for RXCUI: ${rxcui}`);
          continue;
        }

        const properties = detailData.properties;
        
        // Apply the same filtering logic
        if (this.isAminoAcidComposition(properties.name) || 
            this.isComplexInjectableSolution(properties.name)) {
          console.log(`  🚫 Filtering out alternative result: ${properties.name.substring(0, 50)}...`);
          continue;
        }
        
        const result: MedicationSearchResult = {
          rxcui: properties.rxcui,
          name: properties.name,
          tty: properties.tty,
          language: 'ENG'
        };

        console.log(`  ✅ Including alternative result:`, result.name);
        results.push(result);
        
      } catch (error) {
        console.error(`  💥 Error processing RXCUI ${rxcui}:`, error);
      }
    }

    const sortedResults = this.sortResults(results, query);
    console.log('🏆 Alternative sorted results:', sortedResults.length);
    
    return sortedResults;
  }

  private sortResults(results: MedicationSearchResult[], query: string): MedicationSearchResult[] {
    console.log('🔄 Sorting results...');
    
    const queryLower = query.toLowerCase();
    
    const sorted = results
      .sort((a, b) => {
        // Prioritize exact matches
        const aExact = a.name.toLowerCase() === queryLower;
        const bExact = b.name.toLowerCase() === queryLower;
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;

        // Prioritize brand names and simpler medications
        const aPriority = this.getTTYPriority(a.tty);
        const bPriority = this.getTTYPriority(b.tty);
        if (aPriority !== bPriority) return aPriority - bPriority;

        // Prioritize shorter names (usually simpler medications)
        const aLength = a.name.length;
        const bLength = b.name.length;
        if (Math.abs(aLength - bLength) > 20) {
          return aLength - bLength;
        }

        // Then by starts with query
        const aStarts = a.name.toLowerCase().startsWith(queryLower);
        const bStarts = b.name.toLowerCase().startsWith(queryLower);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        
        return a.name.localeCompare(b.name);
      })
      .slice(0, 10);

    console.log('🏆 Sorted results:', sorted.map(r => r.name));
    return sorted;
  }

  private getTTYPriority(tty?: string): number {
    // Prioritize medication types (lower number = higher priority)
    const priorities: { [key: string]: number } = {
      'BN': 1,   // Brand Names (highest priority)
      'PSN': 2,  // Prescribable Names
      'SBD': 3,  // Branded Drugs
      'PIN': 4,  // Precise Ingredients
      'GPCK': 5, // Generic Packs
      'BPCK': 6, // Branded Packs
    };
    return priorities[tty || ''] || 10;
  }

  async getMedicationDetails(rxcui: string): Promise<MedicationDetails | null> {
    try {
      const [propsResponse, relatedResponse] = await Promise.all([
        fetch(`${this.baseUrl}/rxcui/${rxcui}/properties.json`),
        fetch(`${this.baseUrl}/rxcui/${rxcui}/related.json?tty=BN+SCD+SBD`)
      ]);

      if (!propsResponse.ok) {
        console.error('Failed to fetch medication properties');
        return null;
      }

      const propsData = await propsResponse.json();
      const relatedData = relatedResponse.ok ? await relatedResponse.json() : null;

      const properties = propsData.properties;
      if (!properties) {
        return null;
      }

      const details: MedicationDetails = {
        rxcui,
        name: properties.name,
        synonyms: properties.synonym ? [properties.synonym] : [],
        brandNames: [],
        genericName: properties.name,
        strength: properties.strength,
        dosageForm: properties.dosageForm,
        route: properties.route
      };

      // Extract brand names from related concepts
      if (relatedData?.relatedGroup?.conceptGroup) {
        relatedData.relatedGroup.conceptGroup.forEach((group: any) => {
          if (group.tty === 'BN' && group.conceptProperties) {
            group.conceptProperties.forEach((concept: any) => {
              if (concept.name && !details.brandNames.includes(concept.name)) {
                details.brandNames.push(concept.name);
              }
            });
          }
        });
      }

      return details;
    } catch (error) {
      console.error('Error fetching medication details:', error);
      return null;
    }
  }

  async validateMedication(medicationName: string): Promise<boolean> {
    const results = await this.searchMedications(medicationName);
    return results.some(result => 
      result.name.toLowerCase() === medicationName.toLowerCase()
    );
  }
}

export const medicationApiService = new MedicationApiService();
